import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

/**
 * SweetAlert2 Utility Service
 * 
 * Provides reusable SweetAlert2 dialog methods for the entire application.
 * All dialogs use consistent styling and behavior.
 * 
 * @example
 * ```typescript
 * import { SweetAlertUtil } from '@core/services/util/sweetalert.util';
 * 
 * // Confirmation dialog
 * SweetAlertUtil.confirm({
 *   title: 'Delete Item?',
 *   text: 'This action cannot be undone'
 * }).then(result => {
 *   if (result.isConfirmed) {
 *     // Handle confirmation
 *   }
 * });
 * 
 * // Success message
 * SweetAlertUtil.success('Operation completed successfully!');
 * ```
 */
export class SweetAlertUtil {
  /**
   * Default button styling classes
   */
  private static readonly DEFAULT_BUTTON_CLASSES = {
    confirmButton: 'btn font-weight-bold',
    cancelButton: 'btn font-weight-bold'
  };

  /**
   * Default colors
   */
  private static readonly COLORS = {
    primary: '#2b3795',
    secondary: '#6c757d',
    success: '#00ae4d',
    danger: '#dc3545',
    warning: '#ff9800',
    info: '#17a2b8'
  };

  /**
   * Show a success message
   * 
   * @param message - Success message text
   * @param title - Optional title (default: 'Success!')
   * @param callback - Optional callback function
   * @returns Promise<SweetAlertResult>
   * 
   * @example
   * ```typescript
   * SweetAlertUtil.success('Data saved successfully!');
   * ```
   */
  static success(
    message: string,
    title: string = 'Success!',
    callback?: (result: SweetAlertResult) => void
  ): Promise<SweetAlertResult> {
    return Swal.fire({
      title,
      text: message,
      icon: 'success',
      confirmButtonText: 'OK',
      confirmButtonColor: this.COLORS.success,
      buttonsStyling: false,
      customClass: {
        confirmButton: `${this.DEFAULT_BUTTON_CLASSES.confirmButton} btn-success`
      }
    }).then((result) => {
      if (callback) callback(result);
      return result;
    });
  }

  /**
   * Show an error message
   * 
   * @param message - Error message text
   * @param title - Optional title (default: 'Error!')
   * @param callback - Optional callback function
   * @returns Promise<SweetAlertResult>
   * 
   * @example
   * ```typescript
   * SweetAlertUtil.error('Something went wrong!');
   * ```
   */
  static error(
    message: string,
    title: string = 'Error!',
    callback?: (result: SweetAlertResult) => void
  ): Promise<SweetAlertResult> {
    return Swal.fire({
      title,
      text: message,
      icon: 'error',
      confirmButtonText: 'OK',
      confirmButtonColor: this.COLORS.danger,
      buttonsStyling: false,
      customClass: {
        confirmButton: `${this.DEFAULT_BUTTON_CLASSES.confirmButton} btn-danger`
      }
    }).then((result) => {
      if (callback) callback(result);
      return result;
    });
  }

  /**
   * Show a warning message
   * 
   * @param message - Warning message text
   * @param title - Optional title (default: 'Warning!')
   * @param callback - Optional callback function
   * @returns Promise<SweetAlertResult>
   * 
   * @example
   * ```typescript
   * SweetAlertUtil.warning('Please check your input!');
   * ```
   */
  static warning(
    message: string,
    title: string = 'Warning!',
    callback?: (result: SweetAlertResult) => void
  ): Promise<SweetAlertResult> {
    return Swal.fire({
      title,
      text: message,
      icon: 'warning',
      confirmButtonText: 'OK',
      confirmButtonColor: this.COLORS.warning,
      buttonsStyling: false,
      customClass: {
        confirmButton: `${this.DEFAULT_BUTTON_CLASSES.confirmButton} btn-warning`
      }
    }).then((result) => {
      if (callback) callback(result);
      return result;
    });
  }

  /**
   * Show an info message
   * 
   * @param message - Info message text
   * @param title - Optional title (default: 'Info')
   * @param callback - Optional callback function
   * @returns Promise<SweetAlertResult>
   */
  static info(
    message: string,
    title: string = 'Info',
    callback?: (result: SweetAlertResult) => void
  ): Promise<SweetAlertResult> {
    return Swal.fire({
      title,
      text: message,
      icon: 'info',
      confirmButtonText: 'OK',
      confirmButtonColor: this.COLORS.info,
      buttonsStyling: false,
      customClass: {
        confirmButton: `${this.DEFAULT_BUTTON_CLASSES.confirmButton} btn-info`
      }
    }).then((result) => {
      if (callback) callback(result);
      return result;
    });
  }

  /**
   * Show a confirmation dialog
   * 
   * @param options - Confirmation options
   * @returns Promise<SweetAlertResult>
   * 
   * @example
   * ```typescript
   * SweetAlertUtil.confirm({
   *   title: 'Delete Item?',
   *   text: 'This action cannot be undone',
   *   confirmText: 'Yes, Delete',
   *   cancelText: 'Cancel',
   *   type: 'danger'
   * }).then(result => {
   *   if (result.isConfirmed) {
   *     // Handle deletion
   *   }
   * });
   * ```
   */
  static confirm(options: {
    title: string;
    text?: string;
    html?: string;
    confirmText?: string;
    cancelText?: string;
    type?: 'primary' | 'success' | 'danger' | 'warning' | 'info';
    icon?: 'question' | 'warning' | 'info';
    allowOutsideClick?: boolean;
    allowEscapeKey?: boolean;
  }): Promise<SweetAlertResult> {
    const {
      title,
      text,
      html,
      confirmText = 'Yes, Confirm',
      cancelText = 'Cancel',
      type = 'primary',
      icon = 'question',
      allowOutsideClick = false,
      allowEscapeKey = true
    } = options;

    const colorMap = {
      primary: this.COLORS.primary,
      success: this.COLORS.success,
      danger: this.COLORS.danger,
      warning: this.COLORS.warning,
      info: this.COLORS.info
    };

    const buttonClassMap = {
      primary: 'btn-primary',
      success: 'btn-success',
      danger: 'btn-danger',
      warning: 'btn-warning',
      info: 'btn-info'
    };

    return Swal.fire({
      title,
      text,
      html,
      icon,
      showCancelButton: true,
      confirmButtonColor: colorMap[type],
      cancelButtonColor: this.COLORS.secondary,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      allowOutsideClick,
      allowEscapeKey,
      buttonsStyling: false,
      customClass: {
        confirmButton: `${this.DEFAULT_BUTTON_CLASSES.confirmButton} ${buttonClassMap[type]} mr-2`,
        cancelButton: `${this.DEFAULT_BUTTON_CLASSES.cancelButton} btn-secondary`
      }
    });
  }

  /**
   * Show a custom dialog with full control
   * 
   * @param options - SweetAlert2 options
   * @returns Promise<SweetAlertResult>
   * 
   * @example
   * ```typescript
   * SweetAlertUtil.fire({
   *   title: 'Custom Dialog',
   *   html: '<p>Custom HTML content</p>',
   *   icon: 'question',
   *   showCancelButton: true
   * });
   * ```
   */
  static fire(options: SweetAlertOptions): Promise<SweetAlertResult> {
    // Apply default styling if not provided
    if (!options.buttonsStyling) {
      options.buttonsStyling = false;
    }

    if (!options.customClass) {
      options.customClass = this.DEFAULT_BUTTON_CLASSES;
    }

    return Swal.fire(options);
  }

  /**
   * Show a memo approval confirmation dialog
   * 
   * @param memoRef - Memo reference number
   * @returns Promise<SweetAlertResult>
   */
  static confirmApprove(memoRef: string): Promise<SweetAlertResult> {
    return this.confirm({
      title: 'Approve Memo?',
      html: `Are you sure you want to approve <strong>${memoRef}</strong>?`,
      icon: 'question',
      confirmText: '<i class="la la-check"></i> Yes, Approve',
      cancelText: '<i class="la la-close"></i> Cancel',
      type: 'primary'
    });
  }

  /**
   * Show a memo rejection confirmation dialog
   * 
   * @param memoRef - Memo reference number
   * @returns Promise<SweetAlertResult>
   */
  static confirmReject(memoRef: string): Promise<SweetAlertResult> {
    return this.confirm({
      title: 'Reject Memo?',
      html: `Are you sure you want to reject <strong>${memoRef}</strong>?`,
      icon: 'warning',
      confirmText: '<i class="la la-close"></i> Yes, Reject',
      cancelText: '<i class="la la-arrow-left"></i> Cancel',
      type: 'danger'
    });
  }

  /**
   * Show a memo request change confirmation dialog
   * 
   * @param memoRef - Memo reference number
   * @returns Promise<SweetAlertResult>
   */
  static confirmRequestChange(memoRef: string): Promise<SweetAlertResult> {
    return this.confirm({
      title: 'Request for Change?',
      html: `Are you sure you want to request changes for <strong>${memoRef}</strong>?`,
      icon: 'question',
      confirmText: '<i class="la la-edit"></i> Yes, Request Change',
      cancelText: '<i class="la la-arrow-left"></i> Cancel',
      type: 'warning'
    });
  }

  /**
   * Show a remarks required warning
   * 
   * @param action - Action that requires remarks (e.g., 'rejecting', 'requesting changes')
   * @returns Promise<SweetAlertResult>
   */
  static showRemarksRequired(action: string = 'performing this action'): Promise<SweetAlertResult> {
    return this.warning(
      `Please provide remarks before ${action}.`,
      'Remarks Required'
    );
  }

  /**
   * Show a memo save as draft confirmation dialog
   * 
   * @param memoRef - Memo reference number
   * @returns Promise<SweetAlertResult>
   */
  static confirmSaveDraft(memoRef: string): Promise<SweetAlertResult> {
    return this.confirm({
      title: 'Save as Draft?',
      html: `Are you sure you want to save <strong>${memoRef}</strong> as draft?<br><small class="text-muted">You can edit and submit it later.</small>`,
      icon: 'question',
      confirmText: '<i class="la la-save"></i> Yes, Save as Draft',
      cancelText: '<i class="la la-arrow-left"></i> Cancel',
      type: 'primary'
    });
  }

  /**
   * Show a memo submit confirmation dialog
   * 
   * @param memoRef - Memo reference number
   * @returns Promise<SweetAlertResult>
   */
  static confirmSubmit(memoRef: string): Promise<SweetAlertResult> {
    return this.confirm({
      title: 'Submit Memo?',
      html: `Are you sure you want to submit <strong>${memoRef}</strong>?<br><small class="text-muted">This will send the memo for approval.</small>`,
      icon: 'question',
      confirmText: '<i class="la la-paper-plane"></i> Yes, Submit',
      cancelText: '<i class="la la-arrow-left"></i> Cancel',
      type: 'primary'
    });
  }

  /**
   * Show a delete confirmation dialog (reusable across the app)
   * 
   * @param options - Delete confirmation options
   * @returns Promise<SweetAlertResult>
   * 
   * @example
   * ```typescript
   * SweetAlertUtil.confirmDelete({
   *   itemName: 'Designation',
   *   itemIdentifier: 'Manager',
   *   customMessage: 'This action cannot be undone.'
   * }).then(result => {
   *   if (result.isConfirmed) {
   *     // Handle deletion
   *   }
   * });
   * ```
   */
  static confirmDelete(options: {
    itemName?: string;
    itemIdentifier?: string;
    customMessage?: string;
    title?: string;
  }): Promise<SweetAlertResult> {
    const {
      itemName = 'item',
      itemIdentifier,
      customMessage = 'This action cannot be undone.',
      title = 'Delete Item?'
    } = options;

    let html = `Are you sure you want to delete this <strong>${itemName}</strong>?`;

    if (itemIdentifier) {
      html = `Are you sure you want to delete <strong>${itemIdentifier}</strong>?`;
    }

    if (customMessage) {
      html += `<br><small class="text-muted">${customMessage}</small>`;
    }

    return this.confirm({
      title,
      html,
      icon: 'warning',
      confirmText: '<i class="la la-trash"></i> Yes, Delete',
      cancelText: '<i class="la la-arrow-left"></i> Cancel',
      type: 'danger'
    });
  }

  /**
   * Show a generic action confirmation dialog
   * 
   * @param options - Action confirmation options
   * @returns Promise<SweetAlertResult>
   */
  static confirmAction(options: {
    title: string;
    text: string;
    confirmButtonText?: string;
    cancelButtonText?: string;
    icon?: 'warning' | 'info' | 'question';
    type?: 'primary' | 'success' | 'danger' | 'warning' | 'info';
  }): Promise<SweetAlertResult> {
    return this.confirm({
      title: options.title,
      text: options.text,
      confirmText: options.confirmButtonText,
      cancelText: options.cancelButtonText,
      icon: options.icon,
      type: options.type || 'warning'
    });
  }

  /**
   * Show a loading dialog with a spinner
   * 
   * @param message - Loading message text
   * @param title - Optional title
   */
  static loading(message: string = 'Processing your request...', title: string = 'Please Wait...'): void {
    Swal.fire({
      title,
      text: message,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
  }

  /**
   * Close the current SweetAlert dialog
   */
  static close(): void {
    Swal.close();
  }
}

